/**
 * Dialog — modal primitive replacing window.confirm / window.alert.
 * Focus-trapped; ESC and overlay click close it.
 *
 * Props:
 *   open      {boolean}
 *   onClose   {() => void}
 *   title     {string}
 *   children  {React.ReactNode}
 *   className {string} — extra classes for the panel
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const Dialog = ({ open, onClose, title, children, className = '' }) => {
  const panelRef = useRef(null);

  // ESC closes
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();

      // Basic focus trap
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Move focus into panel on open
    const firstFocusable = panelRef.current?.querySelector(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      className="fixed inset-0 z-40 flex items-center justify-center"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`relative z-50 bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 modal-animate ${className}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          {title && (
            <h2
              id="dialog-title"
              className="text-lg font-playfair font-bold text-[#17386E]"
            >
              {title}
            </h2>
          )}
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
};

export default Dialog;
